import { type ReactNode, type ReactElement } from 'react'
import type { SerializedDiamond } from '../types'
import { Table } from '@stuller/stullercom/ui'
import Link from 'next/link'
import { isStringEmpty } from '@stuller/shared/util/core'

interface SerializedDiamondDetailsTableProps {
  serializedDiamond: SerializedDiamond
}

interface DiamondDetailsRowProps {
  header: string
  children: ReactNode
}

/**
 * An individual row of the diamond details table
 */
function DiamondDetailsTableRow ({ header, children }: DiamondDetailsRowProps): ReactElement {
  return (
    <tr>
      <th className='bg-gray-100' scope='row'>
        {header}
      </th>
      <td>
        {children}
      </td>
    </tr>
  )
}

/**
 * Table component for displaying diamond details
 */
function SerializedDiamondDetailsTable ({ serializedDiamond }: SerializedDiamondDetailsTableProps): ReactElement {
  // The fields shown are different for round
  const isRound = serializedDiamond.shape === 'Round'

  return (
    <Table bordered rounded>
      <caption hidden>
        Diamond specifications
      </caption>
      <colgroup>
        <col width='140px' />
      </colgroup>
      <tbody>
        <DiamondDetailsTableRow header='Type'>
          {serializedDiamond.isLabGrown ? 'Lab-Grown ' : ''}{serializedDiamond.stoneFamily}
        </DiamondDetailsTableRow>
        <DiamondDetailsTableRow header='Shape'>
          {serializedDiamond.shape}
        </DiamondDetailsTableRow>
        {isRound && (
          <DiamondDetailsTableRow header='Size (mm)'>
            {serializedDiamond.calibratedSize} mm
          </DiamondDetailsTableRow>
        )}
        {!isRound && (
          <>
            <DiamondDetailsTableRow header='length (mm)'>
              {serializedDiamond.length} mm
            </DiamondDetailsTableRow>
            <DiamondDetailsTableRow header='width (mm)'>
              {serializedDiamond.width} mm
            </DiamondDetailsTableRow>
            <DiamondDetailsTableRow header='L/W'>
              {serializedDiamond.lengthToWidthRatio}:1
            </DiamondDetailsTableRow>
          </>
        )}
        <DiamondDetailsTableRow header='Depth %'>
          {serializedDiamond.depthPercent ?? 'None'}
        </DiamondDetailsTableRow>
        <DiamondDetailsTableRow header='Table %'>
          {serializedDiamond.tablePercent ?? 'None'}
        </DiamondDetailsTableRow>
        <DiamondDetailsTableRow header='Girdle'>
          {serializedDiamond.girdle}{serializedDiamond.girdle2 != null ? ` to ${serializedDiamond.girdle2}` : ''}
        </DiamondDetailsTableRow>
        {isRound && (
          <DiamondDetailsTableRow header='Min Diameter'>
            {serializedDiamond.minDiameter} mm
          </DiamondDetailsTableRow>
        )}
        {isRound && (
          <DiamondDetailsTableRow header='Max Diameter'>
            {serializedDiamond.maxDiameter} mm
          </DiamondDetailsTableRow>
        )}
        <DiamondDetailsTableRow header='Culet'>
          {serializedDiamond.culet}
        </DiamondDetailsTableRow>
        <DiamondDetailsTableRow header='Fluorescence'>
          {serializedDiamond.fluorescence}
        </DiamondDetailsTableRow>
        <DiamondDetailsTableRow header='Report'>
          {!isStringEmpty(serializedDiamond.report?.url)
            ? (
              <Link href={serializedDiamond.report?.url} target='_blank'>
                {serializedDiamond.report?.provider}
              </Link>
            )
            : (
              <>None</>
            )}
        </DiamondDetailsTableRow>
        <DiamondDetailsTableRow header='AGTA'>
          {serializedDiamond.agtaDescription}
        </DiamondDetailsTableRow>
        <DiamondDetailsTableRow header='Notes'>
          {serializedDiamond.notes}
        </DiamondDetailsTableRow>
      </tbody>
    </Table>
  )
}

export {
  SerializedDiamondDetailsTable
}
